/**
 * The activity ticker's rules, pure (server.ts gathers the inputs). Nothing is made up: a row exists only for a real trade, a real competition state
 * read from the competitions API, a reward period or pool read from the contracts, or a real 24 h price move.
 *
 * Order (Bez 2026-10-04):
 *   1. LIVE trades of the last 30 minutes, newest first ("bezbm bought $250 of CADINU · 10s ago"; green buys, red sells), at least TICKER_MIN_USD
 *   2. events: competition started / final hour / ended / winners announced (legacy V3 ones labelled "Legacy #n"), farm reward periods, new farms,
 *      new staking pools
 *   3. movers: +-20 % or more in 24 h, only tokens with at least MOVER_MIN_LIQUIDITY_USD of liquidity, rises and falls
 *   then the older trades of the last 24 h, newest first.
 * With no trade in the last 30 minutes, events and movers alternate.
 * MultiSwap savings (Bez 2026-10-06): "Saved 0.42% via Biswap · 2m ago" from CONFIRMED trade records only (server/multiswapRecord: the
 * trade verified on chain, the price-hunt gain the client claimed kept only then, clamped to 0–500 bps). One line per 10 minutes at most
 * (the most recent wins), at least 1 bps, 24 h, MAX_SAVINGS lines; a recent one follows the live trades, an older one sits among the
 * older trades by time. No wallet is shown; no record → no line.
 * Logos and live competitions (Bez 2026-10-07): every row starts with a round picture (`avatar`): the traded token, the moving token, the
 * competition's token, a staking pool's reward token, the exchange of a saving; the image URLs are the ones the app already resolves
 * (the token list's logo, then utils/getTokenLogoURL by address), tried in order in the browser, and a letter avatar when none loads.
 * Each competition running now (as the Competitions tab counts it: live or upcoming, started, not ended) has its own row right after
 * the live trades, "CADINU competition live · 200,000,000 CADINU prize · ends in 9d 10h", its logo in the tab's green live ring; that
 * row replaces the competition's "is live" and "final hour" events (the same facts), its "started" event stays.
 */

export type TickerTone = 'buy' | 'sell' | 'event' | 'up' | 'down'
/** The round picture at the start of a row. */
export interface TickerAvatar {
  /** image URLs, tried in order (one that fails to load is skipped); none or all failing: the letter avatar */
  srcs: string[]
  /** the letter avatar: the first letter or digit of the token's symbol (or the exchange's name) */
  letter: string
  /** a competition running now: the green live ring of the Competitions tab */
  live?: boolean
  /** no token to show (a farm reward period): this Fluent icon in the disc instead of a letter */
  icon?: string
}
export interface TickerItem {
  id: string
  kind: 'trade' | 'event' | 'mover' | 'saving' | 'live'
  tone: TickerTone
  text: string
  href: string
  /** unix seconds the thing happened */
  at: number
  /** the bar adds "· 10s ago" from `at` */
  ago?: boolean
  /** the bar adds "· ends in 42m" / "· starts in 3h"; `fine`: two units, "· ends in 9d 10h" */
  countdown?: { to: number; word: 'ends' | 'starts'; fine?: boolean }
  /** a saving: the DEX's local logo (read by pages loaded before `avatar` existed) */
  logo?: string
  /** the row's round picture (every row has one) */
  avatar: TickerAvatar
}
export interface TickerOut {
  items: TickerItem[]
  /** how many leading items are trades of the last 30 minutes */
  live: number
  minUsd: number
  builtAt: number
}

export const RECENT_S = 30 * 60
export const OLD_TRADES_S = 24 * 3600
export const MAX_RECENT = 8
export const MAX_OLDER = 4
export const MAX_EVENTS = 6
export const MAX_MOVERS = 4
export const MOVER_PCT = 20
export const SAVINGS_MIN_BPS = 1
export const SAVINGS_MAX_BPS = 500
export const SAVINGS_SPACING_S = 10 * 60
export const MAX_SAVINGS = 3

export interface TradeCandidate {
  id: string
  tx: string
  t: number
  side: 'buy' | 'sell'
  usd: number | null
  symbol: string
  token: string
  wallet: string | null
  /** the /u handle, when the wallet has a profile that is on */
  handle?: string | null
  /** 'cadinudex' or the other exchange's name (shown) */
  venue: string
  /** the token's logo URLs, in the order to try them (server.ts) */
  logos?: string[]
}

export const shortWallet = (w: string) => (w.length > 8 ? `${w.slice(0, 4)}…${w.slice(-2)}` : w)
export const fmtUsdShort = (n: number) => (n >= 10 ? `$${Math.round(n).toLocaleString('en-US')}` : `$${n.toFixed(2)}`)
/** "200,000,000", "1,234.5", "0.25": a prize written out in full (the live competition row) */
export const fmtAmountFull = (n: number) => (n >= 1 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : n.toLocaleString('en-US', { maximumSignificantDigits: 3 }))
/** the letter avatar's letter: the first letter or digit, upper case ("?" when there is none) */
export const letterOf = (name: string) => (name.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 1) || '?').toUpperCase()
/** a token's avatar from the URLs the server resolved (no URL: the letter alone) */
export const tokenAvatar = (symbol: string, srcs: Array<string | null | undefined> = []): TickerAvatar => ({
  srcs: Array.from(new Set(srcs.filter((u): u is string => typeof u === 'string' && u.length > 0))),
  letter: letterOf(symbol),
})

/**
 * One row per transaction and token: a route that splits a sale over two pools is one sale (their sizes add up), and a token a route only passes
 * through (bought in one pool, sold in the next) nets to about nothing and falls under the minimum. A leg without a USD size makes the whole group unpriced.
 */
export function mergeByTx(cands: TradeCandidate[]): TradeCandidate[] {
  const groups = new Map<string, TradeCandidate[]>()
  cands.forEach((c) => {
    const k = `${c.tx}|${c.token.toLowerCase()}|${c.venue}`
    groups.set(k, [...(groups.get(k) ?? []), c])
  })
  return Array.from(groups.values()).map((g) => {
    if (g.length === 1) return g[0]
    const priced = g.every((c) => c.usd !== null)
    const net = g.reduce((t, c) => t + (c.side === 'buy' ? 1 : -1) * (c.usd ?? 0), 0)
    return { ...g[0], t: Math.max(...g.map((c) => c.t)), side: net >= 0 ? ('buy' as const) : ('sell' as const), usd: priced ? Math.abs(net) : null }
  })
}

/** Trades the ticker may show: priced, at least `minUsd`, inside the last 24 hours, one row per transaction and token. */
export function filterTrades(cands: TradeCandidate[], nowS: number, minUsd: number): TradeCandidate[] {
  const seen = new Set<string>()
  return mergeByTx(cands)
    .filter((c) => c.usd !== null && Number.isFinite(c.usd) && c.usd >= minUsd && c.t <= nowS + 60 && nowS - c.t <= OLD_TRADES_S && /^[A-Za-z0-9.$_-]{1,16}$/.test(c.symbol))
    .filter((c) => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })
    .sort((a, b) => b.t - a.t || (a.id < b.id ? 1 : -1))
}

export function tradeItem(c: TradeCandidate): TickerItem {
  const who = c.handle || (c.wallet ? shortWallet(c.wallet) : 'A trader')
  const where = c.venue === 'cadinudex' ? '' : ` on ${c.venue}`
  return {
    id: c.id,
    kind: 'trade',
    tone: c.side === 'buy' ? 'buy' : 'sell',
    text: `${who} ${c.side === 'buy' ? 'bought' : 'sold'} ${fmtUsdShort(c.usd as number)} of ${c.symbol}${where}`,
    href: `/token/${c.token}`,
    at: c.t,
    ago: true,
    avatar: tokenAvatar(c.symbol, c.logos),
  }
}

/** A confirmed MultiSwap trade with a price-hunt gain (server/multiswapRecord TradeRecord.claimedGainBps); nothing else about it is shown. */
export interface SavingCandidate {
  /** the trade's tx hash */
  id: string
  /** block time (unix s) */
  t: number
  gainBps: number
  /** the verified DEX's display name ("Biswap") */
  dexName: string
  /** its local logo (/images/dex/hunt/…) */
  logo: string
}

/** Savings the bar may show: 1–500 bps, last 24 h, newest first, and no two within 10 minutes of each other (the newer one is kept). */
export function filterSavings(cands: SavingCandidate[], nowS: number): SavingCandidate[] {
  const ok = cands
    .filter((c) => Number.isFinite(c.gainBps) && c.gainBps >= SAVINGS_MIN_BPS && c.gainBps <= SAVINGS_MAX_BPS && Number.isFinite(c.t) && c.t <= nowS + 60 && nowS - c.t <= OLD_TRADES_S && /^[A-Za-z0-9 ]{1,24}$/.test(c.dexName))
    .sort((a, b) => b.t - a.t || (a.id < b.id ? 1 : -1))
  const out: SavingCandidate[] = []
  for (const c of ok) {
    if (out.length >= MAX_SAVINGS) break
    const last = out[out.length - 1]
    if (!last || last.t - c.t >= SAVINGS_SPACING_S) out.push(c)
  }
  return out
}

export function savingItem(c: SavingCandidate): TickerItem {
  const pct = (Math.floor(c.gainBps) / 100).toFixed(2)
  return { id: `saving:${c.id}`, kind: 'saving', tone: 'up', text: `Saved ${pct}% via ${c.dexName}`, href: '/swap', at: c.t, ago: true, logo: c.logo, avatar: tokenAvatar(c.dexName, [c.logo]) }
}

export interface MoverInput {
  address: string
  symbol: string
  change24h: number | null
  liquidityUsd: number | null
  /** the token's logo URLs, in the order to try them */
  logos?: string[]
}
/** +-20 % or more in 24 h with real liquidity; biggest moves first. */
export function moverItems(list: MoverInput[], nowS: number, minLiquidityUsd: number): TickerItem[] {
  return list
    .filter((t) => t.change24h !== null && Number.isFinite(t.change24h) && Math.abs(t.change24h) >= MOVER_PCT && (t.liquidityUsd ?? 0) >= minLiquidityUsd && /^[A-Za-z0-9.$_-]{1,16}$/.test(t.symbol))
    .sort((a, b) => Math.abs(b.change24h as number) - Math.abs(a.change24h as number))
    .slice(0, MAX_MOVERS)
    .map((t) => {
      const up = (t.change24h as number) > 0
      return { id: `mover:${t.address}`, kind: 'mover' as const, tone: up ? ('up' as const) : ('down' as const), text: `${t.symbol} ${up ? '+' : '−'}${Math.abs(t.change24h as number).toFixed(1)}% in 24h`, href: `/token/${t.address}`, at: nowS, avatar: tokenAvatar(t.symbol, t.logos) }
    })
}

export interface CompetitionInput {
  id: number
  legacy: boolean
  phase: string
  startTime: number
  endTime: number
  symbol: string
  prize: string | null
  /** the prize written out in full for the live row ("200,000,000 CADINU"); null when it cannot be read */
  prizeFull?: string | null
  winners: number
  href: string
  /** the competition token's logo URLs, in the order to try them */
  logos?: string[]
}
/** What a competition's state says right now, or nothing (an old, quiet competition has no row). */
export function competitionItem(c: CompetitionInput, nowS: number): TickerItem | null {
  const label = `${c.legacy ? 'Legacy' : 'Competition'} #${c.id} · ${c.symbol}`
  const prize = c.prize ? ` · prize ${c.prize}` : ''
  const base = { kind: 'event' as const, tone: 'event' as const, href: c.href, avatar: tokenAvatar(c.symbol, c.logos) }
  const id = `${c.legacy ? 'v3' : 'v4'}:${c.id}`
  if (c.phase === 'live') {
    if (c.endTime > nowS && c.endTime - nowS <= 3600) return { ...base, id: `${id}:last`, text: `${label}: final hour`, at: nowS, countdown: { to: c.endTime, word: 'ends' } }
    if (nowS - c.startTime <= 24 * 3600) return { ...base, id: `${id}:start`, text: `${label} started${prize}`, at: c.startTime, ago: true }
    return { ...base, id: `${id}:live`, text: `${label} is live${prize}`, at: c.startTime, countdown: { to: c.endTime, word: 'ends' } }
  }
  if (c.phase === 'upcoming' && c.startTime > nowS && c.startTime - nowS <= 24 * 3600) return { ...base, id: `${id}:soon`, text: `${label}${prize}`, at: nowS, countdown: { to: c.startTime, word: 'starts' } }
  if ((c.phase === 'awaiting_result' || c.phase === 'refundable') && nowS - c.endTime <= 48 * 3600) return { ...base, id: `${id}:ended`, text: `${label} ended · results being checked`, at: c.endTime, ago: true }
  if (c.phase === 'settled' && c.winners > 0 && nowS - c.endTime <= 7 * 24 * 3600) return { ...base, id: `${id}:won`, text: `${label}: winners announced`, at: c.endTime }
  return null
}

const RUNNING = new Set(['live', 'upcoming'])
/**
 * Running now by the clock, the rule of the Competitions tab (components/Menu/hooks/useCompetitionLive isLiveNow): phase live or upcoming,
 * the start passed, the end not; an ended or settled one never counts.
 */
export const isCompetitionRunning = (c: Pick<CompetitionInput, 'phase' | 'startTime' | 'endTime'>, nowS: number) =>
  RUNNING.has(c.phase) && Number.isFinite(c.startTime) && Number.isFinite(c.endTime) && c.startTime <= nowS && nowS < c.endTime
const prefixOf = (c: Pick<CompetitionInput, 'legacy' | 'id'>) => `${c.legacy ? 'v3' : 'v4'}:${c.id}`

/**
 * One row per competition running now, in the tab's order (the current contract first, then the one ending soonest, then the newest):
 * "CADINU competition live · 200,000,000 CADINU prize · ends in 9d 10h", linking to that competition. The countdown is written in the
 * browser from `countdown.to`; the bar drops the row once that time has passed.
 */
export function liveCompetitionItems(list: CompetitionInput[], nowS: number): TickerItem[] {
  return list
    .filter((c) => isCompetitionRunning(c, nowS))
    .sort((a, b) => Number(a.legacy) - Number(b.legacy) || a.endTime - b.endTime || b.id - a.id)
    .map((c) => ({
      id: `${prefixOf(c)}:now`,
      kind: 'live' as const,
      tone: 'event' as const,
      text: `${c.symbol} competition live${c.prizeFull ? ` · ${c.prizeFull} prize` : ''}`,
      href: c.href,
      at: c.startTime,
      countdown: { to: c.endTime, word: 'ends' as const, fine: true },
      avatar: { ...tokenAvatar(c.symbol, c.logos), live: true },
    }))
}

/** Events newest first, a final hour before anything else; legacy ones after the current ones. */
export function orderEvents(items: TickerItem[]): TickerItem[] {
  const rank = (i: TickerItem) => (i.id.endsWith(':last') ? 0 : i.id.startsWith('v3:') ? 2 : 1)
  return [...items].sort((a, b) => rank(a) - rank(b) || b.at - a.at).slice(0, MAX_EVENTS)
}

const alternate = (a: TickerItem[], b: TickerItem[]) => {
  const out: TickerItem[] = []
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if (a[i]) out.push(a[i])
    if (b[i]) out.push(b[i])
  }
  return out
}

/**
 * The bar's rows in their order. `trades` must already be filtered (filterTrades), `savings` too (filterSavings); `live` are the rows of
 * the competitions running now (liveCompetitionItems): right after the live trades, and their own "is live" / "final hour" events go.
 */
export function orderTicker(
  trades: TradeCandidate[],
  events: TickerItem[],
  movers: TickerItem[],
  nowS: number,
  savings: SavingCandidate[] = [],
  live: TickerItem[] = [],
): { items: TickerItem[]; live: number } {
  const recent = trades.filter((t) => nowS - t.t <= RECENT_S).slice(0, MAX_RECENT).map(tradeItem)
  const olderTrades = trades.filter((t) => nowS - t.t > RECENT_S).slice(0, MAX_OLDER).map(tradeItem)
  const recentSavings = savings.filter((s) => nowS - s.t <= RECENT_S).map(savingItem)
  const olderSavings = savings.filter((s) => nowS - s.t > RECENT_S).map(savingItem)
  // an older saving takes its place among the older trades by time (a stable sort keeps the trades' own order)
  const older = olderSavings.length ? [...olderTrades, ...olderSavings].sort((a, b) => b.at - a.at) : olderTrades
  const covered = new Set(live.map((l) => l.id.replace(/:now$/, '')))
  const ev = orderEvents(events.filter((e) => !(/:(live|last)$/.test(e.id) && covered.has(e.id.replace(/:(live|last)$/, '')))))
  const middle = recent.length ? [...ev, ...movers] : alternate(ev, movers)
  return { items: [...recent, ...live, ...recentSavings, ...middle, ...older], live: recent.length }
}

/** "10s ago", "2m ago", "3h ago", "2d ago" */
export function agoText(at: number, nowS: number): string {
  const d = Math.max(0, nowS - at)
  if (d < 60) return `${Math.max(1, Math.floor(d))}s ago`
  if (d < 3600) return `${Math.floor(d / 60)}m ago`
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`
  return `${Math.floor(d / 86400)}d ago`
}
/** "42m", "3h", "13d" (rounded down; under a minute "<1m") */
export function inText(to: number, nowS: number): string {
  const d = Math.max(0, to - nowS)
  if (d < 60) return '<1m'
  if (d < 3600) return `${Math.floor(d / 60)}m`
  if (d < 86400) return `${Math.floor(d / 3600)}h`
  return `${Math.floor(d / 86400)}d`
}
/** "9d 10h", "5h 12m", "42m" (two units, rounded down, a zero second unit left out; under a minute "<1m") */
export function inTextFine(to: number, nowS: number): string {
  const d = Math.max(0, to - nowS)
  if (d < 60) return '<1m'
  const two = (a: number, ua: string, b: number, ub: string) => (b > 0 ? `${a}${ua} ${b}${ub}` : `${a}${ua}`)
  if (d < 3600) return `${Math.floor(d / 60)}m`
  if (d < 86400) return two(Math.floor(d / 3600), 'h', Math.floor((d % 3600) / 60), 'm')
  return two(Math.floor(d / 86400), 'd', Math.floor((d % 86400) / 3600), 'h')
}
/** The countdown words of an item at `nowS` ("ends in 9d 10h"), or null (no countdown, or its time has passed). */
export function countdownWords(i: TickerItem, nowS: number): string | null {
  if (!i.countdown || i.countdown.to <= nowS) return null
  return `${i.countdown.word} in ${(i.countdown.fine ? inTextFine : inText)(i.countdown.to, nowS)}`
}
/** The words the bar shows for an item at `nowS`. */
export function itemWords(i: TickerItem, nowS: number): string {
  if (i.ago) return `${i.text} · ${agoText(i.at, nowS)}`
  const cd = countdownWords(i, nowS)
  return cd ? `${i.text} · ${cd}` : i.text
}
/** A live competition row whose end has passed in the browser is not shown (the server's answer can be a minute old). */
export const isShown = (i: TickerItem, nowS: number | null) => !(i.kind === 'live' && nowS !== null && i.countdown && i.countdown.to <= nowS)
